import { useEffect, useRef, useState, type RefObject } from "react";
import { useAuth } from "../auth/AuthProvider";
import { config } from "../config";
import { startWhip, WhipSession } from "./whip";
import { useCamera } from "./useCamera";
import { useLocation, GeoPoint } from "./useLocation";
import { postLocationUpdate } from "./locationStream";
import CameraControls from "./CameraControls";
import LocationGate from "./LocationGate";
import LocationBadge from "./LocationBadge";
import ShareBar from "./ShareBar";

type Phase = "idle" | "preview" | "live" | "ending";
type StreamProfileKey = "veryLow" | "low" | "balanced" | "high";
type TranscribeLanguage = "es-US" | "en-US" | "nl-NL";

const TRANSCRIBE_LANGUAGES: Array<{ code: TranscribeLanguage; label: string }> = [
  { code: "es-US", label: "Spanish" },
  { code: "en-US", label: "English" },
  { code: "nl-NL", label: "Dutch" },
];

const STREAM_PROFILES: Record<StreamProfileKey, {
  label: string;
  hint: string;
  videoBitrateKbps: number;
  audioBitrateKbps: number;
  constraints: MediaTrackConstraints;
}> = {
  veryLow: {
    label: "Very low",
    hint: "Emergency mode for extremely limited data.",
    videoBitrateKbps: 512,
    audioBitrateKbps: 24,
    constraints: {
      width: { ideal: 480 },
      height: { ideal: 270 },
      frameRate: { ideal: 15 },
    },
  },
  low: {
    label: "Low bandwidth",
    hint: "Best for weak Wi-Fi or mobile data.",
    videoBitrateKbps: 700,
    audioBitrateKbps: 32,
    constraints: {
      width: { ideal: 640 },
      height: { ideal: 360 },
      frameRate: { ideal: 24 },
    },
  },
  balanced: {
    label: "Balanced",
    hint: "A safer default for most connections.",
    videoBitrateKbps: 1400,
    audioBitrateKbps: 48,
    constraints: {
      width: { ideal: 960 },
      height: { ideal: 540 },
      frameRate: { ideal: 30 },
    },
  },
  high: {
    label: "High quality",
    hint: "Use only when the connection is stable.",
    videoBitrateKbps: 2800,
    audioBitrateKbps: 64,
    constraints: {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30 },
    },
  },
};

const STREAM_PROFILE_RANK: Record<StreamProfileKey, number> = {
  veryLow: 0,
  low: 1,
  balanced: 2,
  high: 3,
};

function connectionProfileHint(): StreamProfileKey {
  const connection = (navigator as Navigator & { connection?: { effectiveType?: string; saveData?: boolean } }).connection;
  if (!connection) return "balanced";
  if (connection.saveData) return "veryLow";
  switch (connection.effectiveType) {
    case "slow-2g":
    case "2g":
      return "veryLow";
    case "3g":
      return "low";
    case "4g":
      return "balanced";
    default:
      return "balanced";
  }
}

function pickAdaptiveProfile(selected: StreamProfileKey): StreamProfileKey {
  const hint = connectionProfileHint();
  return STREAM_PROFILE_RANK[hint] < STREAM_PROFILE_RANK[selected] ? hint : selected;
}

export default function BroadcastPage() {
  const { user, getIdToken } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const sessionRef = useRef<WhipSession | null>(null);
  const tokenRef = useRef<string | null>(null);
  const actionLockRef = useRef(false);
  const audioFrameRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const [phase, setPhase] = useState<Phase>("idle");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [title, setTitle] = useState("");
  const [broadcastId, setBroadcastId] = useState<string | null>(null);
  const [profile, setProfile] = useState<StreamProfileKey>(() => pickAdaptiveProfile("balanced"));
  const [transcribeLanguage, setTranscribeLanguage] = useState<TranscribeLanguage>("es-US");
  const [simulcastAvailable, setSimulcastAvailable] = useState<{ facebook: boolean; youtube: boolean }>({ facebook: false, youtube: false });
  const [simulcastSelected, setSimulcastSelected] = useState<{ facebook: boolean; youtube: boolean; facebookAccount: boolean; youtubeAccount: boolean }>({ facebook: false, youtube: false, facebookAccount: false, youtubeAccount: false });
  type SocialConn = { configured: boolean; connected: boolean; accountName: string | null; destination?: string; destinations?: Array<{ id: string; name: string }> };
  const [socialConn, setSocialConn] = useState<{ facebook: SocialConn; youtube: SocialConn }>({
    facebook: { configured: false, connected: false, accountName: null },
    youtube: { configured: false, connected: false, accountName: null },
  });

  const refreshSocialStatus = async () => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const r = await fetch(`${config.gatewayUrl}/social-live/status`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d && d.ok) setSocialConn({ facebook: d.facebook, youtube: d.youtube });
    } catch { /* non-fatal */ }
  };

  const setFacebookDestination = async (id: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;
      await fetch(`${config.gatewayUrl}/social-live/facebook/destination`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      setSocialConn((s) => ({ ...s, facebook: { ...s.facebook, destination: id } }));
    } catch { /* non-fatal */ }
  };

  const connectSocial = async (provider: "facebook" | "youtube") => {
    const token = await getIdToken();
    if (!token) return;
    const w = window.open(`${config.gatewayUrl}/social-live/auth/${provider}/start?jwt=${encodeURIComponent(token)}`,
      "social-connect", "width=560,height=720");
    const onMsg = (e: MessageEvent) => {
      if (e.data && e.data.socialLive) {
        window.removeEventListener("message", onMsg);
        void refreshSocialStatus();
      }
    };
    window.addEventListener("message", onMsg);
    const poll = window.setInterval(() => {
      if (w && w.closed) { window.clearInterval(poll); void refreshSocialStatus(); }
    }, 1200);
  };
  const [iceState, setIceState] = useState<RTCIceConnectionState>("new");
  const [err, setErr] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [uplinkKbps, setUplinkKbps] = useState<number | null>(null);
  const [hudBelowPreview, setHudBelowPreview] = useState(false);
  const hasStreamTitle = title.trim().length > 0;

  // Stream location updates upstream while we're live
  const onLocationUpdate = async (point: GeoPoint) => {
    const session = sessionRef.current;
    const token = tokenRef.current;
    if (!session || !token) return;
    await postLocationUpdate(config.gatewayUrl, token, session.resourceUrl, point);
  };

  const camera = useCamera({
    onVideoTrackChanged: (track) => sessionRef.current?.replaceVideoTrack(track),
    onAudioTrackChanged: (track) => sessionRef.current?.replaceAudioTrack(track),
  });
  const location = useLocation(onLocationUpdate);

  useEffect(() => {
    void refreshSocialStatus();
    fetch(`${config.gatewayUrl}/simulcast/targets`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.ok) setSimulcastAvailable({ facebook: !!d.facebook, youtube: !!d.youtube });
      })
      .catch(() => {});
  }, []);

  // Immediately request location when the page mounts — this is a hard gate.
  useEffect(() => {
    location.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateHudPlacement = () => {
      const landscape = window.matchMedia("(orientation: landscape)").matches;
      const coarsePointer = window.matchMedia("(pointer: coarse)").matches;
      setHudBelowPreview(landscape && (coarsePointer || window.innerHeight <= 650));
    };

    updateHudPlacement();
    window.addEventListener("resize", updateHudPlacement);
    window.addEventListener("orientationchange", updateHudPlacement);
    return () => {
      window.removeEventListener("resize", updateHudPlacement);
      window.removeEventListener("orientationchange", updateHudPlacement);
    };
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (phase !== "live") return;
      const video = videoRef.current;
      const stream = camera.state.stream;
      if (!video || !stream) return;

      if (document.hidden) {
        return;
      }

      video.srcObject = stream;
      video.play().catch((e) => {
        console.warn("camera preview resume failed", e);
      });
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pageshow", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handleVisibility);
    };
  }, [camera.state.stream, phase]);

  // Bind preview to current camera stream
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const stream = camera.state.stream;
    video.srcObject = stream;

    if (!stream) {
      return;
    }

    const startPlayback = () => {
      video.play().catch((e) => {
        console.warn("camera preview play() failed", e);
      });
    };

    video.muted = true;
    video.playsInline = true;

    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      startPlayback();
    } else {
      video.addEventListener("loadedmetadata", startPlayback, { once: true });
    }

    return () => {
      video.removeEventListener("loadedmetadata", startPlayback);
    };
  }, [camera.state.stream]);

  useEffect(() => {
    const stream = camera.state.stream;
    if (!stream || typeof window.AudioContext === "undefined") {
      setAudioLevel(0);
      return;
    }

    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      setAudioLevel(0);
      return;
    }

    let cancelled = false;
    const context = new AudioContext();
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.2;

    try {
      const source = context.createMediaStreamSource(stream);
      source.connect(analyser);
    } catch (e) {
      console.warn("audio meter setup failed", e);
      context.close().catch(() => {});
      setAudioLevel(0);
      return;
    }

    audioContextRef.current = context;
    const buffer = new Uint8Array(analyser.fftSize);

    const tick = () => {
      if (cancelled) return;
      analyser.getByteTimeDomainData(buffer);
      let sum = 0;
      for (const sample of buffer) {
        const centered = (sample - 128) / 128;
        sum += centered * centered;
      }
      const rms = Math.sqrt(sum / buffer.length);
      const nextLevel = Math.max(0, Math.min(100, Math.round(rms * 180)));
      setAudioLevel(nextLevel);
      audioFrameRef.current = window.requestAnimationFrame(tick);
    };

    void context.resume().catch(() => {});
    tick();

    return () => {
      cancelled = true;
      if (audioFrameRef.current !== null) {
        window.cancelAnimationFrame(audioFrameRef.current);
        audioFrameRef.current = null;
      }
      setAudioLevel(0);
      context.close().catch(() => {});
      if (audioContextRef.current === context) {
        audioContextRef.current = null;
      }
    };
  }, [camera.state.stream]);

  // Live elapsed clock
  useEffect(() => {
    if (phase !== "live") return;
    const t = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  // Cleanup on unmount
  useEffect(() => () => {
    sessionRef.current?.stop();
    camera.stop();
    location.stop();
    if (audioFrameRef.current !== null) {
      window.cancelAnimationFrame(audioFrameRef.current);
    }
    audioContextRef.current?.close().catch(() => {});
  }, [camera.stop, location.stop]);

  useEffect(() => {
    if (phase !== "live") {
      setUplinkKbps(null);
      return;
    }

    let cancelled = false;
    let previousBytes = 0;
    let previousAt = performance.now();

    const sampleStats = async () => {
      const session = sessionRef.current;
      if (!session || cancelled) return;

      try {
        const stats = await session.pc.getStats();
        let bytesSent = 0;

        stats.forEach((report) => {
          if (report.type === "outbound-rtp" && !report.isRemote && typeof report.bytesSent === "number") {
            bytesSent += report.bytesSent;
          }
        });

        const now = performance.now();
        if (previousBytes === 0) {
          previousBytes = bytesSent;
          previousAt = now;
          return;
        }

        const deltaBytes = Math.max(0, bytesSent - previousBytes);
        const deltaSeconds = Math.max(0.25, (now - previousAt) / 1000);
        const kbps = (deltaBytes * 8) / 1000 / deltaSeconds;
        setUplinkKbps(Math.round(kbps));
        previousBytes = bytesSent;
        previousAt = now;
      } catch (e) {
        console.warn("upload stats unavailable", e);
      }
    };

    void sampleStats();
    const timer = window.setInterval(() => {
      void sampleStats();
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      setUplinkKbps(null);
    };
  }, [phase]);

  useEffect(() => {
    const connection = (navigator as Navigator & {
      connection?: EventTarget & { effectiveType?: string; saveData?: boolean };
    }).connection;
    if (!connection) return;

    const applyAdaptiveBitrate = async () => {
      const session = sessionRef.current;
      if (!session || phase !== "live") return;
      const adaptiveProfile = pickAdaptiveProfile(profile);
      const next = STREAM_PROFILES[adaptiveProfile];
      await session.updateBitrates(next.videoBitrateKbps, next.audioBitrateKbps).catch((e) => {
        console.warn("adaptive bitrate update failed", e);
      });
    };

    const onChange = () => {
      void applyAdaptiveBitrate();
    };

    connection.addEventListener?.("change", onChange);
    void applyAdaptiveBitrate();

    return () => {
      connection.removeEventListener?.("change", onChange);
    };
  }, [phase, profile]);

  // Hard gate: location must be acquired before any camera/streaming UI shows.
  if (!location.state.current) {
    return <LocationGate state={location.state} onRetry={location.start} />;
  }

  function acquireActionLock() {
    if (actionLockRef.current) return false;
    actionLockRef.current = true;
    setIsTransitioning(true);
    return true;
  }

  function releaseActionLock() {
    actionLockRef.current = false;
    setIsTransitioning(false);
  }

  async function startPreview() {
    if (!acquireActionLock()) return;
    setErr(null);
    try {
      const adaptiveProfile = pickAdaptiveProfile(profile);
      const started = await camera.start(STREAM_PROFILES[adaptiveProfile].constraints);
      if (started) {
        const video = videoRef.current;
        if (video) {
          video.srcObject = started;
          video.muted = true;
          video.playsInline = true;
          await video.play().catch((e) => {
            console.warn("camera preview play() failed", e);
          });
        }
        setPhase("preview");
      }
    } finally {
      releaseActionLock();
    }
  }

  async function goLive() {
    if (!camera.state.stream || !location.state.current) return;
    if (!hasStreamTitle) {
      setErr("Stream title is required before going live.");
      return;
    }
    if (!acquireActionLock()) return;
    setErr(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error("Missing auth token — sign in again");
      tokenRef.current = token;
      const nextBroadcastId = `brd-${crypto.randomUUID().slice(0, 8)}`;
      setBroadcastId(nextBroadcastId);

      const slug = title.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40) || "untitled";
      const endpoint = `${config.gatewayUrl}/whip/${encodeURIComponent(slug)}`;

      const fix = location.state.current;
      const adaptiveProfile = pickAdaptiveProfile(profile);
      const session = await startWhip({
        endpoint,
        token,
        stream: camera.state.stream,
        geo: { lat: fix.lat, lon: fix.lon, accuracyM: fix.accuracyM, at: fix.at },
        broadcastId: nextBroadcastId,
        transcribeLanguage,
        streamTitle: title.trim(),
        simulcast: [
          ...(simulcastSelected.facebook && simulcastAvailable.facebook ? ["facebook"] : []),
          ...(simulcastSelected.youtube && simulcastAvailable.youtube ? ["youtube"] : []),
          ...(simulcastSelected.facebookAccount && socialConn.facebook.connected ? ["facebook-account"] : []),
          ...(simulcastSelected.youtubeAccount && socialConn.youtube.connected ? ["youtube-account"] : []),
        ],
        videoBitrateKbps: STREAM_PROFILES[adaptiveProfile].videoBitrateKbps,
        audioBitrateKbps: STREAM_PROFILES[adaptiveProfile].audioBitrateKbps,
        onIceState: setIceState,
      });
      sessionRef.current = session;
      setPhase("live");
      setElapsed(0);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Failed to start stream");
    } finally {
      releaseActionLock();
    }
  }

  async function endStream() {
    if (!acquireActionLock()) return;
    setPhase("ending");
    try {
      await sessionRef.current?.stop();
      sessionRef.current = null;
      tokenRef.current = null;
      setBroadcastId(null);
      camera.stop();
      setPhase("idle");
    } finally {
      releaseActionLock();
    }
  }

  const streamHud = (className = "broadcast__hud") => (
    <div className={className} aria-label="Stream status">
      <div className="broadcast__hud-row">
        <span className={`broadcast__live-indicator ${phase === "live" ? "is-live" : ""}`}>
          <span className="broadcast__live-dot" />
          {phase === "live" ? "LIVE" : "Preview"}
        </span>
        <span className="broadcast__hud-pill">
          {uplinkKbps !== null ? `${uplinkKbps} kbps up` : "Waiting for stats"}
        </span>
      </div>

      <div className="broadcast__audio-meter" aria-hidden="true">
        <span style={{ width: `${Math.max(2, audioLevel)}%` }} />
      </div>

      <div className="broadcast__hud-row broadcast__hud-row--bottom">
        <span>Audio level {audioLevel}%</span>
        <span>{camera.state.micEnabled ? "Mic on" : "Mic off"}</span>
      </div>
    </div>
  );

  return (
    <div className="broadcast">
      <header className="broadcast__header">
        <div>
          <h1>Go Live</h1>
          <p className="muted">Signed in as {user?.displayName}</p>
        </div>
        <div className="broadcast__header-right">
          <LocationBadge state={location.state} />
          {phase === "live" && (
            <div className="live-badge">
              <span className="live-dot" /> LIVE · {formatElapsed(elapsed)}
            </div>
          )}
        </div>
      </header>

      <div className="broadcast__workspace">
        <div className="broadcast__left">
          <div className="broadcast__panel-head">
            <div>
              <p className="broadcast__eyebrow">Camera preview</p>
              <h2 className="broadcast__panel-title">Preview feed</h2>
            </div>
            <span className="broadcast__state-pill">
              {phase === "live" ? "Broadcasting" : phase === "preview" ? "Ready" : "Idle"}
            </span>
          </div>

          <div className="broadcast__stage">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="preview-video"
            />
            {phase === "idle" && (
              <div className="preview-overlay">
                <p>Tap Open camera to start the preview.</p>
              </div>
            )}
            {!camera.state.camEnabled && phase !== "idle" && (
              <div className="preview-overlay"><p>📷 Camera hidden</p></div>
            )}
            {phase !== "idle" && !hudBelowPreview && streamHud()}

          </div>
          {phase !== "idle" && hudBelowPreview && streamHud("broadcast__hud broadcast__hud--inline")}

          {(simulcastAvailable.facebook || simulcastAvailable.youtube || socialConn.facebook.configured || socialConn.youtube.configured) && (
            <div className="simulcast-toggles" aria-label="Also stream to social platforms">
              <span className="simulcast-toggles__label">Also go live on:</span>
              {simulcastAvailable.facebook && (
                <label className="simulcast-toggles__item">
                  <input type="checkbox" checked={simulcastSelected.facebook} disabled={phase === "live"}
                    onChange={(e) => setSimulcastSelected((s) => ({ ...s, facebook: e.target.checked }))} />
                  Facebook (station page)
                </label>
              )}
              {simulcastAvailable.youtube && (
                <label className="simulcast-toggles__item">
                  <input type="checkbox" checked={simulcastSelected.youtube} disabled={phase === "live"}
                    onChange={(e) => setSimulcastSelected((s) => ({ ...s, youtube: e.target.checked }))} />
                  YouTube (station)
                </label>
              )}
              {socialConn.facebook.configured && (socialConn.facebook.connected ? (
                <span className="simulcast-toggles__group">
                  <label className="simulcast-toggles__item" title={socialConn.facebook.accountName ?? ""}>
                    <input type="checkbox" checked={simulcastSelected.facebookAccount} disabled={phase === "live"}
                      onChange={(e) => setSimulcastSelected((s) => ({ ...s, facebookAccount: e.target.checked }))} />
                    Facebook →
                  </label>
                  <select
                    className="simulcast-toggles__dest"
                    value={socialConn.facebook.destination ?? "me"}
                    disabled={phase === "live"}
                    onChange={(e) => void setFacebookDestination(e.target.value)}
                    aria-label="Facebook publish destination"
                  >
                    {(socialConn.facebook.destinations ?? []).map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </span>
              ) : (
                <button type="button" className="simulcast-toggles__connect" onClick={() => void connectSocial("facebook")}>
                  Connect Facebook account
                </button>
              ))}
              {socialConn.youtube.configured && (socialConn.youtube.connected ? (
                <label className="simulcast-toggles__item" title={socialConn.youtube.accountName ?? ""}>
                  <input type="checkbox" checked={simulcastSelected.youtubeAccount} disabled={phase === "live"}
                    onChange={(e) => setSimulcastSelected((s) => ({ ...s, youtubeAccount: e.target.checked }))} />
                  YouTube ({socialConn.youtube.accountName ?? "my channel"})
                </label>
              ) : (
                <button type="button" className="simulcast-toggles__connect" onClick={() => void connectSocial("youtube")}>
                  Connect YouTube account
                </button>
              ))}
            </div>
          )}
          <label className="broadcast__field broadcast__field--full">
            <input
              aria-label="Stream title"
              aria-invalid={!hasStreamTitle && phase !== "idle"}
              placeholder="Stream title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={phase === "live"}
              maxLength={80}
              required
            />
          </label>
          <p className="muted small broadcast__field-hint">Required before starting the broadcast.</p>

          <div className="broadcast__title-actions">
            {phase === "idle" && (
              <button className="primary broadcast__cta" type="button" onClick={startPreview} disabled={camera.state.loading || isTransitioning}>
                {camera.state.loading || isTransitioning ? "Opening camera…" : "Open camera"}
              </button>
            )}
            {phase === "preview" && (
              <>
                <button className="ghost" type="button" onClick={() => endStream()} disabled={isTransitioning}>Cancel</button>
                <button className="primary danger" type="button" onClick={goLive} disabled={isTransitioning || !hasStreamTitle}>
                  {isTransitioning ? "Starting…" : "Start broadcasting"}
                </button>
              </>
            )}
            {phase === "live" && (
              <button className="primary danger" type="button" onClick={endStream} disabled={isTransitioning}>
                {isTransitioning ? "Stopping…" : "End stream"}
              </button>
            )}
            {phase === "ending" && <span className="muted">Stopping…</span>}
            {isTransitioning && phase !== "ending" && <span className="muted">Working…</span>}
          </div>

          <CameraControls
            state={camera.state}
            onFlip={camera.flipCamera}
            onPickAudio={camera.switchToAudioDevice}
            onZoomChange={camera.setZoomLevel}
            onToggleMic={camera.toggleMic}
            onToggleCam={camera.toggleCam}
            disabled={camera.state.loading || phase === "idle" || isTransitioning}
          />
        </div>

        <div className="broadcast__right">
          <div className="broadcast__panel-head">
            <div>
              <p className="broadcast__eyebrow">Broadcast details</p>
              <h2 className="broadcast__panel-title">Stream settings</h2>
            </div>
          </div>

          <div className="broadcast__details">
            <div className="broadcast__settings">
              <div className="broadcast__field">
                <span>Connection profile</span>
                <div className="broadcast__profiles" role="tablist" aria-label="Connection profile">
                  {(Object.keys(STREAM_PROFILES) as StreamProfileKey[]).map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={`broadcast__profile ${profile === key ? "is-active" : ""}`}
                      onClick={() => setProfile(key)}
                      disabled={phase === "live" || isTransitioning}
                      aria-pressed={profile === key}
                    >
                      <strong>{STREAM_PROFILES[key].label}</strong>
                      <span>{STREAM_PROFILES[key].videoBitrateKbps} kbps video</span>
                    </button>
                  ))}
                </div>
                <p className="muted small broadcast__profile-hint">{STREAM_PROFILES[profile].hint}</p>
              </div>
              <label className="broadcast__field">
                <span>Transcript language</span>
                <select
                  value={transcribeLanguage}
                  onChange={(e) => setTranscribeLanguage(e.target.value as TranscribeLanguage)}
                  disabled={phase === "live" || isTransitioning}
                >
                  {TRANSCRIBE_LANGUAGES.map((language) => (
                    <option key={language.code} value={language.code}>{language.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <ShareBar
              title={title}
              locationLabel={formatLocationLabel(location.state.current)}
              broadcastId={broadcastId}
              captureImage={() => captureBroadcastStill(videoRef)}
            />

            {phase !== "idle" && (
              <p className="muted small">
                Connection: <strong>{iceState}</strong>
                {camera.state.facing && ` · Facing: ${camera.state.facing}`}
              </p>
            )}
            {(err || camera.state.error) && <p className="err">{err ?? camera.state.error}</p>}

            <div className="tips">
              <p className="tips__title">Tips for a good stream</p>
              <ul>
                <li>Keep the screen on so location keeps updating.</li>
                <li>If you're indoors, accuracy may be lower (±100–500 m). Move near a window when possible.</li>
                <li>Use Chrome, Edge, Safari 16+, or Firefox 110+.</li>
                <li>Streams auto-save to the archive after you end. Location is saved with them.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatElapsed(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

function formatLocationLabel(point: GeoPoint | null) {
  if (!point) return "";
  return `${point.lat.toFixed(4)}, ${point.lon.toFixed(4)}`;
}

async function captureBroadcastStill(videoRef: RefObject<HTMLVideoElement>) {
  const video = videoRef.current;
  if (!video || video.videoWidth <= 0 || video.videoHeight <= 0) return null;

  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((result) => resolve(result), "image/jpeg", 0.9);
  });
  if (!blob) return null;

  return new File([blob], `amigoe-live-${Date.now()}.jpg`, { type: "image/jpeg" });
}
